const AUDIT_IGNORED_FIELDS = new Set([
    "id",
    "documentId",
    "createdAt",
    "updatedAt",
    "publishedAt",
]);

const isRecord = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

const normalizedComparableValue = (value) => {
    if (Array.isArray(value)) {
        return value.map(normalizedComparableValue);
    }
    if (isRecord(value)) {
        return Object.fromEntries(Object.keys(value).sort()
            .map((key) => [key, normalizedComparableValue(value[key])]));
    }
    return value ?? null;
};

const valuesAreEqual = (left, right) => JSON.stringify(normalizedComparableValue(left))
    === JSON.stringify(normalizedComparableValue(right));

const topLevelValues = (value) => isRecord(value)
    ? Object.fromEntries(Object.entries(value).filter(([key]) => !AUDIT_IGNORED_FIELDS.has(key)))
    : {};

export function getAuditChanges(auditLog) {
    const before = topLevelValues(auditLog?.before);
    const after = topLevelValues(auditLog?.after);
    const patch = topLevelValues(auditLog?.patch);
    const patchFields = Object.keys(patch);
    const fields = patchFields.length
        ? patchFields
        : [...new Set([...Object.keys(before), ...Object.keys(after)])];

    return fields
        .filter((field) => !valuesAreEqual(before[field], after[field]))
        .map((field) => ({
            field,
            before: before[field],
            after: after[field],
        }));
}

const displayText = (value) => {
    if (!isRecord(value)) return "";
    return value.title
        || value.name
        || value.fullname
        || value.label
        || value.username
        || value.documentId
        || "";
};

const translateIfAvailable = (translate, key) => {
    const translated = translate(key);
    return translated === key ? null : translated;
};

const isIsoDate = (value) => typeof value === "string"
    && /^\d{4}-\d{2}-\d{2}(?:T|\s)/.test(value)
    && !Number.isNaN(new Date(value).getTime());

export function formatAuditValue(value, translate, formatDate) {
    if (value === null || value === undefined || value === "") {
        return translate("events-center.value-empty");
    }
    if (typeof value === "boolean") {
        return translate(`events-center.value.${value ? "yes" : "no"}`);
    }
    if (isIsoDate(value)) return formatDate(value);
    if (Array.isArray(value)) {
        if (!value.length) return translate("events-center.value-empty");
        return value.map((item) => formatAuditValue(item, translate, formatDate)).join("، ");
    }
    if (isRecord(value)) {
        const label = displayText(value);
        if (label) return String(label);
        const values = Object.values(value)
            .filter((item) => ["string", "number", "boolean"].includes(typeof item))
            .map((item) => formatAuditValue(item, translate, formatDate));
        return values.length ? values.join("، ") : translate("events-center.value.complex");
    }
    if (typeof value === "string") {
        return translateIfAvailable(translate, `events-center.audit-value.${value}`)
            || translateIfAvailable(translate, value)
            || value;
    }
    return String(value);
}
